import { useCallback } from "react";
import { generatePath, useNavigate } from "react-router";

import { useCustomMutation } from "@/hooks/use-graphql-client";

import { PATHS } from "@/routes/paths";

import { START_GUIDE_QUERY } from "@/components/features/_shared/repository/start-guide/query/schema";
import { PlanList_PlanListPage_Query } from "@/components/features/items/list/plan/model/repository/query/fetch-plan-list/query";
import { notifyUserEventInfo } from "@/components/features/side-effect/notify-user-event-info";
import { useLoadingScreen } from "@/components/features/side-effect/show-loading-screen-to-user/_hooks/use-loading-screen";

import { viewModelToPlanCustomMailSettingInputConverter, viewModelToPlanInputConverter } from "./composition";
import { DayTripPlanCreateMutation } from "./mutation";

import type { ProductDayTripPlanFormModel } from "../../../type";

type Props = {
	reset: () => void;
};

export const useDayTripPlanEditMutation = ({ reset }: Props) => {
	const { showLoadingScreenCallback, hideLoadingScreenCallback } = useLoadingScreen();
	const { mutationFunc } = useCustomMutation(DayTripPlanCreateMutation);

	const navigate = useNavigate();
	const planListPagePath = (hotelID: string) => generatePath(PATHS.HOTEL.ITEMS.PLAN.TOP, { hotelID });
	const navigateToPlanListPage = useCallback(
		(hotelID: string) => {
			navigate(planListPagePath(hotelID));
		},
		[navigate],
	);

	const onUpdatePlan = useCallback(
		async (
			planViewModel: ProductDayTripPlanFormModel["ViewModel"], //
		) => {
			showLoadingScreenCallback();

			try {
				const planInput = viewModelToPlanInputConverter({ viewModel: planViewModel });
				const planCustomMailSettingInput = viewModelToPlanCustomMailSettingInputConverter({
					viewModel: planViewModel,
				});

				// プラン作成
				const { data: planData, errors: planErrors } = await mutationFunc({
					variables: {
						input: planInput,
						dayTripPlanCustomMailSettingInput: planCustomMailSettingInput,
					},
					refetchQueries: [
						{
							query: PlanList_PlanListPage_Query,
							variables: {
								hotelID: planInput.hotelID,
							},
						},
						{
							query: START_GUIDE_QUERY,
							variables: { hotelID: planInput.hotelID },
						},
					],
				});

				if (planErrors || !planData?.planCreate?.plan?.planID) {
					return;
				}

				notifyUserEventInfo({
					variant: "default",
					title: "日帰りプランを作成しました",
				});
				reset();
				setTimeout(() => navigateToPlanListPage(planInput.hotelID), 300);
			} finally {
				hideLoadingScreenCallback();
			}
		},
		[hideLoadingScreenCallback, mutationFunc, navigateToPlanListPage, reset, showLoadingScreenCallback],
	);

	return { onUpdatePlan };
};
